import {
  useRef,
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { createPortal } from 'react-dom';
import {
  PeopleTeamList,
  usePeopleTeamFilteredList,
  type PeopleTeamItem,
} from '@/shared/components/people-team-list/PeopleTeamList';
import { useMentionSerializer } from '@/shared/hooks/useMentionSerializer';

interface MentionEditorProps {
  placeholder?: string;
  onTextChange: (text: string) => void;
  onMention: (type: 'user' | 'team', id: string, name: string) => void;
  teamsLabel?: string;
  peopleLabel?: string;
}

export interface MentionEditorRef {
  clear: () => void;
}

export const MentionEditor = forwardRef<MentionEditorRef, MentionEditorProps>(
  (
    {
      placeholder,
      onTextChange,
      onMention,
      teamsLabel = 'Teams',
      peopleLabel = 'People',
    },
    ref
  ) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const [mentionOpen, setMentionOpen] = useState(false);
    const [mentionQuery, setMentionQuery] = useState('');
    const [mentionRange, setMentionRange] = useState<{
      node: Text;
      atIdx: number;
      endIdx: number;
    } | null>(null);
    const [selectedIdx, setSelectedIdx] = useState(0);

    const allItems = usePeopleTeamFilteredList(mentionQuery);
    const getStructuredText = useMentionSerializer(editorRef);

    useImperativeHandle(ref, () => ({
      clear: () => {
        if (editorRef.current) {
          while (editorRef.current.firstChild) {
            editorRef.current.removeChild(editorRef.current.firstChild);
          }
        }
        setMentionOpen(false);
        setMentionQuery('');
        setMentionRange(null);
      },
    }));

    const closeMention = useCallback(() => {
      setMentionOpen(false);
      setMentionQuery('');
      setMentionRange(null);
      setSelectedIdx(0);
    }, []);

    const handleInput = useCallback(() => {
      // Browsers leave behind <br> or empty nodes after deleting all
      // text, which prevents the CSS :empty placeholder from showing.
      if (editorRef.current && !getStructuredText().trim()) {
        while (editorRef.current.firstChild) {
          editorRef.current.removeChild(editorRef.current.firstChild);
        }
      }

      onTextChange(getStructuredText());

      const sel = window.getSelection();
      if (!sel || !sel.rangeCount) {
        closeMention();
        return;
      }

      const range = sel.getRangeAt(0);
      const textNode = range.startContainer;
      if (textNode.nodeType !== Node.TEXT_NODE) {
        closeMention();
        return;
      }

      const text = (textNode.textContent || '').substring(0, range.startOffset);
      const atIdx = text.lastIndexOf('@');

      if (
        atIdx === -1 ||
        (atIdx > 0 && text[atIdx - 1] !== ' ' && text[atIdx - 1] !== '\n')
      ) {
        closeMention();
        return;
      }

      const query = text.substring(atIdx + 1);
      setMentionQuery(query);
      setMentionRange({
        node: textNode as Text,
        atIdx,
        endIdx: range.startOffset,
      });
      setMentionOpen(true);
      setSelectedIdx(0);
    }, [closeMention, getStructuredText, onTextChange]);

    const insertMention = useCallback(
      (item: PeopleTeamItem) => {
        if (!mentionRange || !editorRef.current) return;
        const { node, atIdx, endIdx } = mentionRange;

        const mention = document.createElement('span');
        mention.className = 'mention';
        mention.contentEditable = 'false';
        const entityType = item.type === 'team' ? 'team' : 'user';
        mention.setAttribute('data-type', entityType);
        mention.setAttribute('data-id', item.id);
        mention.textContent = `@${item.name}`;

        const before = (node.textContent || '').substring(0, atIdx);
        const after = (node.textContent || '').substring(endIdx);
        node.textContent = before;

        const afterNode = document.createTextNode(`\u00A0${after}`);
        const parent = node.parentNode!;
        parent.insertBefore(mention, node.nextSibling);
        parent.insertBefore(afterNode, mention.nextSibling);

        const sel = window.getSelection();
        if (sel) {
          const newRange = document.createRange();
          newRange.setStart(afterNode, 1);
          newRange.collapse(true);
          sel.removeAllRanges();
          sel.addRange(newRange);
        }

        closeMention();
        onMention(item.type === 'team' ? 'team' : 'user', item.id, item.name);
        onTextChange(getStructuredText());
      },
      [mentionRange, closeMention, onMention, onTextChange, getStructuredText]
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (!mentionOpen) return;

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedIdx((prev) => Math.min(prev + 1, allItems.length - 1));
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedIdx((prev) => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter') {
          e.preventDefault();
          if (allItems[selectedIdx]) {
            insertMention(allItems[selectedIdx]);
          }
        }
      },
      [mentionOpen, allItems, selectedIdx, insertMention]
    );

    // Close dropdown on Escape — native capture-phase listener ensures
    // this fires before Radix Dialog's document-level handler
    useEffect(() => {
      if (!mentionOpen) return;

      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopImmediatePropagation();
          closeMention();
        }
      };

      document.addEventListener('keydown', handleEscape, true);
      return () => document.removeEventListener('keydown', handleEscape, true);
    }, [mentionOpen, closeMention]);

    // Close dropdown on outside click
    useEffect(() => {
      if (!mentionOpen) return;

      const handleClick = (e: MouseEvent) => {
        if (
          editorRef.current &&
          !editorRef.current.contains(e.target as Node) &&
          !(e.target as Element)?.closest('.mention-dropdown')
        ) {
          closeMention();
        }
      };
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }, [mentionOpen, closeMention]);

    // Position dropdown near caret (viewport-fixed, portaled)
    const dropdownRef = useRef<HTMLDivElement>(null);
    const showDropdown = mentionOpen && allItems.length > 0;
    const [dropdownPos, setDropdownPos] = useState({
      inlineStart: 0,
      top: 0,
      flipAbove: false,
    });

    useEffect(() => {
      if (!mentionOpen || !editorRef.current) return;
      const sel = window.getSelection();
      if (!sel || !sel.rangeCount) return;
      const rect = sel.getRangeAt(0).getBoundingClientRect();
      const wrapper = editorRef.current.closest('.mention-editor-wrapper');
      if (!wrapper) return;
      const isRtl = getComputedStyle(wrapper).direction === 'rtl';

      const dropdownMaxH = 200;
      const spaceBelow = window.innerHeight - rect.bottom;
      const flipAbove =
        spaceBelow < dropdownMaxH + 8 && rect.top > dropdownMaxH + 8;

      setDropdownPos({
        inlineStart: isRtl ? window.innerWidth - rect.right : rect.left,
        top: flipAbove ? rect.top - 4 : rect.bottom + 4,
        flipAbove,
      });
    }, [mentionOpen, mentionQuery]);

    // Prevent Radix Dialog from closing when clicking the
    // portaled dropdown (Radix listens for native pointerdown
    // on document — stopPropagation keeps it from bubbling up)
    useEffect(() => {
      if (!showDropdown) return;
      const el = dropdownRef.current;
      if (!el) return;
      const stop = (e: Event): void => e.stopPropagation();
      el.addEventListener('pointerdown', stop);
      return () => el.removeEventListener('pointerdown', stop);
    }, [showDropdown]);

    return (
      <div className="mention-editor-wrapper relative">
        <div
          ref={editorRef}
          contentEditable
          className="signal-editor w-full min-h-[72px] p-0 text-[15px] leading-relaxed text-foreground border-none bg-transparent outline-none whitespace-pre-wrap break-words empty:before:content-[attr(data-placeholder)] empty:before:text-muted-foreground/50 empty:before:pointer-events-none [&_.mention]:inline [&_.mention]:text-[#5e6ad2] [&_.mention]:font-medium [&_.mention]:bg-[rgba(94,106,210,0.08)] [&_.mention]:rounded-sm [&_.mention]:px-0.5"
          data-placeholder={placeholder}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          suppressContentEditableWarning
        />

        {showDropdown &&
          createPortal(
            <div
              ref={dropdownRef}
              className="mention-dropdown fixed min-w-[240px] bg-popover border border-border rounded-md shadow-[0_8px_32px_rgba(0,0,0,0.10)] z-[100] max-h-[200px] overflow-y-auto p-1"
              style={{
                insetInlineStart: dropdownPos.inlineStart,
                top: dropdownPos.top,
                transform: dropdownPos.flipAbove
                  ? 'translateY(-100%)'
                  : undefined,
              }}
              onMouseDown={(e) => e.preventDefault()}
            >
              <PeopleTeamList
                query={mentionQuery}
                teamsLabel={teamsLabel}
                peopleLabel={peopleLabel}
                highlightedIndex={selectedIdx}
                onSelect={insertMention}
                onMouseEnterItem={setSelectedIdx}
                preventFocusSteal
              />
            </div>,
            document.body
          )}
      </div>
    );
  }
);

MentionEditor.displayName = 'MentionEditor';
